/**
 * The lead queue (spec 7: All Open Leads, My Leads, Today's Leads). Age counts up live on every
 * row, because speed to lead is the one number an agent fully controls. Filters live in the URL,
 * so a supervisor can send "unassigned, new" as a link. Ticked rows take Salesforce's bulk
 * actions: Change Status and Change Owner.
 */
import {
  CALL_TYPES,
  consentTypeLabel,
  DISQUALIFY_REASONS,
  formatPhone,
  leadSourceLabel,
  type LeadSummary,
} from "@tlcrm/shared";
import { useState } from "react";
import { useSearchParams } from "react-router";

import { errorMessage } from "../api/client";
import { useBulkLeads, useLeads, useUsers } from "../api/hooks";
import { useMe } from "../auth/session";
import { useToast } from "../components/Toasts";
import { DataTable, type Column } from "../components/DataTable";
import { Icon } from "../components/Icon";
import { EligibilityFlags } from "../components/kit";
import { LeadStatusMark } from "../components/ui";
import { age } from "../lib/format";
import { dial } from "../lib/dial";
import { useNow } from "../lib/useNow";

const STATUSES = [
  ["open", "Open"],
  ["new", "New"],
  ["working", "Working"],
  ["contacted", "Contacted"],
  ["converted", "Converted"],
  ["disqualified", "Disqualified"],
] as const;

const OWNERS = [
  ["", "Everyone"],
  ["me", "Mine"],
  ["unassigned", "Unassigned"],
] as const;

function Age({ lead, now }: { lead: LeadSummary; now: number }) {
  const started = lead.firstContactedAt ? new Date(lead.firstContactedAt) : null;
  if (started) {
    // Once reached, the number that matters is how long it took.
    const took = started.getTime() - new Date(lead.receivedAt).getTime();
    return (
      <span className="age is-reached" title={`Reached ${age(took)} after it came in`}>
        <span aria-hidden="true">✓</span> {age(took).replace(/ \d+s$/, "")}
        <span className="sr-only"> to reach</span>
      </span>
    );
  }
  const ms = now - new Date(lead.receivedAt).getTime();
  return (
    <span
      className={`age${ms < 5 * 60_000 ? " is-hot" : ms > 30 * 60_000 ? " is-cold" : ""}`}
      title="Waiting since it arrived"
    >
      {age(ms)}
    </span>
  );
}

export function Leads() {
  const { me } = useMe();
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "open";
  const owner = params.get("owner") ?? "";
  const callType = params.get("callType") ?? "";
  const today = params.get("today") === "1";
  const now = useNow(1000);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const leads = useLeads({
    status,
    owner: owner || undefined,
    callType: callType || undefined,
    today: today ? "1" : undefined,
  });
  const rows = leads.data?.pages.flatMap((p) => p.items);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const columns: Column<LeadSummary>[] = [
    { header: "Age", cell: (l) => <Age lead={l} now={now} />, width: 84 },
    { header: "Name", cell: (l) => <span className="primary">{l.contactName}</span> },
    {
      header: "Phone",
      cell: (l) =>
        l.phone ? (
          <button
            className="btn btn-ghost btn-sm mono"
            onClick={() => dial(l.phone!)}
            title="Dial on the desk"
          >
            <Icon name="phone" />
            {formatPhone(l.phone)}
          </button>
        ) : (
          <span className="t3">–</span>
        ),
    },
    { header: "Call Type", cell: (l) => l.callType ?? <span className="t3">–</span> },
    {
      header: "Source",
      // The line names the inbound call well enough on its own; a vendor names its own leads.
      cell: (l) => (l.line ? `${l.line} line` : (l.vendor ?? leadSourceLabel(l.source))),
    },
    { header: "State", cell: (l) => l.state ?? <span className="t3">–</span>, width: 64 },
    {
      header: "Before you dial",
      cell: (l) => (
        <EligibilityFlags
          state={l.state}
          language={l.language}
          doNotCall={l.doNotCall}
          licensedStates={me.licensedStates}
          now={now}
          compact
        />
      ),
    },
    {
      header: "Consent",
      cell: (l) =>
        l.consentType === "none" ? (
          <span className="mark mark-ochre">None</span>
        ) : (
          <span className="sub" title={consentTypeLabel(l.consentType)}>
            {l.consentType === "express_written" ? "Written" : "Express"}
          </span>
        ),
    },
    { header: "Status", cell: (l) => <LeadStatusMark status={l.status} /> },
    { header: "Outcome", cell: (l) => l.callOutcome ?? <span className="t3">–</span> },
    { header: "Owner", cell: (l) => l.owner?.fullName ?? <span className="t3">Unassigned</span> },
    {
      header: "Tries",
      cell: (l) => <span className="mono">{l.attempts}</span>,
      num: true,
      width: 60,
    },
  ];

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Leads</h1>
          <p>
            Every inquiry, newest first. A lead from a number already on file is filed on that
            person.
          </p>
        </div>
      </div>
      <div className="toolbar">
        <div className="seg" role="tablist" aria-label="Status">
          {STATUSES.map(([v, l]) => (
            <button
              key={v}
              role="tab"
              aria-selected={status === v}
              onClick={() => setParam("status", v === "open" ? "" : v)}
            >
              {l}
            </button>
          ))}
        </div>
        <div className="seg" role="tablist" aria-label="Owner">
          {OWNERS.map(([v, l]) => (
            <button
              key={l}
              role="tab"
              aria-selected={owner === v}
              onClick={() => setParam("owner", v)}
            >
              {l}
            </button>
          ))}
        </div>
        <label className="check">
          <input
            type="checkbox"
            checked={today}
            onChange={(e) => setParam("today", e.target.checked ? "1" : "")}
          />
          Today's leads
        </label>
        <select
          className="field"
          value={callType}
          onChange={(e) => setParam("callType", e.target.value)}
          aria-label="Call Type"
        >
          <option value="">Every call type</option>
          {CALL_TYPES.map(([v, l]) => (
            <option key={v} value={v}>
              {l}
            </option>
          ))}
        </select>
        <span className="meta">
          {rows ? `${rows.length}${leads.hasNextPage ? "+" : ""} leads` : ""}
        </span>
      </div>
      {selected.size > 0 && <BulkBar ids={[...selected]} onDone={() => setSelected(new Set())} />}
      <DataTable
        columns={columns}
        rows={rows}
        href={(l) => `/leads/${l.id}`}
        selection={{ selected, onChange: setSelected }}
        loading={leads.isPending}
        error={leads.error}
        onRetry={() => void leads.refetch()}
        empty={{ icon: "inbox", title: "No leads here", body: "Try another status or owner." }}
        hasMore={leads.hasNextPage}
        loadingMore={leads.isFetchingNextPage}
        onMore={() => void leads.fetchNextPage()}
      />
    </div>
  );
}

function BulkBar({ ids, onDone }: { ids: string[]; onDone: () => void }) {
  const { can } = useMe();
  const users = useUsers();
  const bulk = useBulkLeads();
  const toast = useToast();
  const [status, setStatus] = useState("");
  const [owner, setOwner] = useState("");
  const apply = async () => {
    try {
      const res = await bulk.mutateAsync({
        ids,
        ...(status && {
          status: status.startsWith("dq:") ? "disqualified" : status,
          ...(status.startsWith("dq:") && { disqualifyReason: status.slice(3) }),
        }),
        ...(owner && { ownerId: owner === "none" ? null : owner }),
      });
      toast(`${res.changed} ${res.changed === 1 ? "lead" : "leads"} changed.`);
      onDone();
    } catch (err) {
      toast(errorMessage(err), { error: true });
    }
  };
  return (
    <div className="bulk" role="region" aria-label="Bulk actions">
      <b>{ids.length} selected</b>
      <select
        className="field"
        value={status}
        onChange={(e) => setStatus(e.target.value)}
        aria-label="Change Status"
      >
        <option value="">Change Status…</option>
        <option value="working">Working</option>
        <option value="contacted">Contacted</option>
        {DISQUALIFY_REASONS.map(([v, l]) => (
          <option key={v} value={`dq:${v}`}>
            Closed lost: {l}
          </option>
        ))}
      </select>
      {can("records:reassign") && (
        <select
          className="field"
          value={owner}
          onChange={(e) => setOwner(e.target.value)}
          aria-label="Change Owner"
        >
          <option value="">Change Owner…</option>
          <option value="none">Unassigned</option>
          {(users.data ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.fullName}
            </option>
          ))}
        </select>
      )}
      <button className="btn btn-sm" onClick={onDone}>
        Clear
      </button>
      <button
        className="btn btn-sm btn-primary"
        style={{ marginLeft: 0 }}
        disabled={(!status && !owner) || bulk.isPending}
        onClick={() => void apply()}
      >
        Apply
      </button>
    </div>
  );
}
